import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Button, message, Upload, Table, Modal, Alert, Space, Tooltip, Tag, Checkbox, Row, Col } from 'antd';
import { ArrowLeftOutlined, EyeOutlined, DownloadOutlined, InfoCircleOutlined, InboxOutlined, SyncOutlined, DeleteOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MolStarViewer from '../../components/results/protein/structure/MolStarViewer';
import { createTask, getTasks, cancelTask, TaskResponse } from '../../utils/taskManager';
import { shouldDisplayInList } from '../../utils/taskManager';

const { Title, Paragraph, Text } = Typography;

type EquiDockTaskResult = {
  success?: boolean;
  docked_ligand_pdb?: string;
  complex_pdb?: string;
  structure_info?: {
    chain_mapping: Record<string, string>;
    final_chains: string[];
    total_atoms: number;
    receptor_chains: string[];
    ligand_chains: string[];
  };
  docker_output?: string;
  // 统一任务格式
  results?: Array<{
    sequence_id: string;
    result: {
      docked_ligand_pdb: string;
      complex_pdb: string;
      structure_info: {
        chain_mapping: Record<string, string>;
        final_chains: string[];
        total_atoms: number;
        receptor_chains: string[];
        ligand_chains: string[];
      };
      docker_output: string;
    };
    success: boolean;
    error: string | null;
  }>;
};

export default function EquiDockAsyncPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [receptorFile, setReceptorFile] = useState<File | null>(null);
  const [ligandFile, setLigandFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedPdb, setSelectedPdb] = useState<string | null>(null);

  const refreshTasks = async () => {
    try {
      const list = await getTasks(0, 50, undefined, 'molecular_interaction', 'equidock');
      const deletedTaskIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      const filteredList = list.filter(task => !deletedTaskIds.includes(task.id));
      const displayList = filteredList.filter(shouldDisplayInList);
      setTasks(displayList);
    } catch (e) {
      console.error('获取任务列表失败:', e);
    }
  };

  useEffect(() => {
    refreshTasks();
    const interval = setInterval(refreshTasks, 5000);
    return () => clearInterval(interval);
  }, []);

  const readFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const submitTask = async () => {
    if (!receptorFile || !ligandFile) {
      message.error('请上传受体和配体PDB文件');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const receptorContent = await readFileContent(receptorFile);
      const ligandContent = await readFileContent(ligandFile);

      const task = await createTask({
        name: `EquiDock对接: ${receptorFile.name} + ${ligandFile.name}`,
        service_name: 'molecular_interaction',
        task_type: 'equidock',
        handler_name: 'equidock_dock_json',
        parameters: {
          receptor_content: receptorContent,
          receptor_filename: receptorFile.name,
          ligand_content: ligandContent,
          ligand_filename: ligandFile.name
        },
        priority: 5,
      });

      message.success(`任务已提交：${task.id}`);
      await refreshTasks();
    } catch (e: any) {
      setError(e?.message || '提交任务失败');
      message.error(`提交任务失败: ${e?.message || '未知错误'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelTask = async (taskId: string) => {
    try {
      await cancelTask(taskId);
      message.success('任务取消成功');
      await refreshTasks();
    } catch (error) {
      console.error('取消任务失败:', error);
      message.error('取消任务失败');
    }
  };

  const handleDeleteTask = (taskId: string) => {
    const deletedTaskIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
    deletedTaskIds.push(taskId);
    localStorage.setItem('deletedTaskIds', JSON.stringify(deletedTaskIds));
    setTasks(tasks.filter(task => task.id !== taskId));
    message.success('任务已从列表中删除');
  };

  const handleSelectTask = (taskId: string, checked: boolean) => {
    if (checked) {
      setSelectedTaskIds([...selectedTaskIds, taskId]);
    } else {
      setSelectedTaskIds(selectedTaskIds.filter(id => id !== taskId));
    }
  };

  const handleReceptorFileChange = (info: any) => {
    if (info.file.status === 'removed') { setReceptorFile(null); return; }
    const fileObj = info.file.originFileObj || (info.file.status !== 'uploading' ? info.file : null);
    if (fileObj instanceof File) setReceptorFile(fileObj);
  };

  const handleLigandFileChange = (info: any) => {
    if (info.file.status === 'removed') { setLigandFile(null); return; }
    const fileObj = info.file.originFileObj || (info.file.status !== 'uploading' ? info.file : null);
    if (fileObj instanceof File) setLigandFile(fileObj);
  };

  const receptorUploadProps = { name: 'file', multiple: false, maxCount: 1, accept: '.pdb', beforeUpload: () => false, onChange: handleReceptorFileChange } as const;
  const ligandUploadProps = { name: 'file', multiple: false, maxCount: 1, accept: '.pdb', beforeUpload: () => false, onChange: handleLigandFileChange } as const;

  const handleViewStructure = (task: TaskResponse, structureType: 'complex' | 'ligand') => {
    const result = task.result as EquiDockTaskResult;
    let pdbContent = '';
    
    // Handle both direct result format and unified task format
    if (result?.results?.[0]?.result) {
      const resultData = result.results[0].result;
      pdbContent = structureType === 'complex' ? resultData.complex_pdb : resultData.docked_ligand_pdb;
    } else if (result) {
      pdbContent = structureType === 'complex' ? result.complex_pdb || '' : result.docked_ligand_pdb || '';
    }

    if (!pdbContent) {
      message.error('没有可用的结构数据');
      return;
    }

    setSelectedPdb(pdbContent);
    setViewerVisible(true);
  };

  const handleDownloadStructure = (task: TaskResponse, structureType: 'complex' | 'ligand') => {
    const result = task.result as EquiDockTaskResult;
    let pdbContent = '';
    let filename = '';
    
    // Handle both direct result format and unified task format
    if (result?.results?.[0]?.result) {
      const resultData = result.results[0].result;
      pdbContent = structureType === 'complex' ? resultData.complex_pdb : resultData.docked_ligand_pdb;
    } else if (result) {
      pdbContent = structureType === 'complex' ? result.complex_pdb || '' : result.docked_ligand_pdb || '';
    }

    filename = structureType === 'complex' 
      ? `equidock_complex_${task.id}.pdb`
      : `equidock_ligand_${task.id}.pdb`;

    if (!pdbContent) {
      message.error('没有可用的结构数据');
      return;
    }

    const blob = new Blob([pdbContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 获取任务状态标签
  const getStatusTag = (task: TaskResponse) => {
    switch (task.status) {
      case 'pending':
        return <Tag color="blue">等待中</Tag>;
      case 'queued':
        return <Tag color="blue">队列中</Tag>;
      case 'running':
        return <Tag color="orange">运行中</Tag>;
      case 'completed':
        return <Tag color="green">已完成</Tag>;
      case 'failed':
        return <Tag color="red">失败</Tag>;
      case 'cancelled':
        return <Tag color="gray">已取消</Tag>;
      case 'timeout':
        return <Tag color="red">超时</Tag>;
      default:
        return <Tag>{task.status}</Tag>;
    }
  };

  // 表格列定义
  const columns = [
    { 
      title: '选择', 
      key: 'selection', 
      width: 60,
      render: (_: any, rec: TaskResponse) => (
        <Checkbox 
          checked={selectedTaskIds.includes(rec.id)}
          onChange={(e) => handleSelectTask(rec.id, e.target.checked)}
          disabled={rec.status === 'running' || rec.status === 'pending'}
        />
      )
    },
    { title: '任务ID', dataIndex: 'id', key: 'id', width: 280, ellipsis: true },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (s: string) => <Tag color={s === 'completed' ? 'green' : s === 'failed' ? 'red' : 'blue'}>{s}</Tag> },
    { title: '进度', dataIndex: 'progress', key: 'progress', width: 100 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 200 },
    { title: '操作', key: 'action', render: (_: any, rec: TaskResponse) => {
      // EquiDock结果检测逻辑 - 适配统一任务格式
      const hasResult = rec.status === 'completed' && rec.result && (
        // 检查直接的结果
        (rec.result.complex_pdb || rec.result.docked_ligand_pdb) ||
        // 检查统一格式中的results数组
        (rec.result.results && Array.isArray(rec.result.results) && rec.result.results.length > 0 && 
         rec.result.results[0] && rec.result.results[0].result && 
         (rec.result.results[0].result.complex_pdb || rec.result.results[0].result.docked_ligand_pdb)) ||
        // 检查是否有任何结果数据
        (rec.result.success === true) ||
        // 检查artifacts中的结果
        (rec.result.artifacts && Object.keys(rec.result.artifacts).length > 0)
      );
      const canDelete = rec.status !== 'running' && rec.status !== 'pending';
      const canCancel = rec.status === 'running' || rec.status === 'pending' || rec.status === 'queued';
      console.log(`任务 ${rec.id} 结果状态:`, { hasResult, result: rec.result, canDelete, canCancel, status: rec.status });
      return (
        <Space>
          <Button size="small" onClick={() => handleViewStructure(rec, 'complex')} disabled={!hasResult}>查看结果</Button>
          {canCancel && (
            <Button 
              size="small" 
              type="default"
              style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
              icon={<StopOutlined />} 
              onClick={() => handleCancelTask(rec.id)}
            >
              取消任务
            </Button>
          )}
          {canDelete && (
            <Button 
              size="small" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDeleteTask(rec.id)}
            >
              删除
            </Button>
          )}
        </Space>
      );
    }},
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* 头部导航 */}
      <div style={{ marginBottom: '24px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/interaction')}
          style={{ marginBottom: '16px' }}
        >
          返回分子互作
        </Button>
        
        <Title level={2}>EquiDock 蛋白质对接</Title>
        <Paragraph>
          EquiDock 是一个基于深度学习的蛋白质-蛋白质对接工具，能够预测蛋白质复合物的三维结构。
          上传受体和配体的PDB文件，系统将自动进行对接并生成复合物结构。
        </Paragraph>
      </div>

      {/* 上部：任务提交表单 */}
      <Card title="提交EquiDock对接任务" style={{ marginBottom: '24px' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={submitTask}
        >
          <Row gutter={24}>
            {/* 受体文件上传 */}
            <Col xs={24} md={12}>
              <Form.Item
                label="受体蛋白质文件 (PDB)"
                required
                help="上传受体蛋白质的PDB结构文件"
              >
                <Upload.Dragger
                  {...receptorUploadProps}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {receptorFile ? receptorFile.name : '点击或拖拽上传受体PDB文件'}
                  </p>
                  <p className="ant-upload-hint">
                    支持.pdb格式文件
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>

            {/* 配体文件上传 */}
            <Col xs={24} md={12}>
              <Form.Item
                label="配体蛋白质文件 (PDB)"
                required
                help="上传配体蛋白质的PDB结构文件"
              >
                <Upload.Dragger
                  {...ligandUploadProps}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {ligandFile ? ligandFile.name : '点击或拖拽上传配体PDB文件'}
                  </p>
                  <p className="ant-upload-hint">
                    支持.pdb格式文件
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>
          </Row>

          {/* 错误信息 */}
          {error && (
            <Alert
              message="错误"
              description={error}
              type="error"
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: '16px' }}
            />
          )}

          {/* 提交按钮 */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={!receptorFile || !ligandFile}
              size="large"
              style={{ width: '200px' }}
            >
              {submitting ? '提交中...' : '开始EquiDock对接'}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 下部：任务列表和结果 */}
      <Card 
        title="EquiDock任务列表" 
        extra={
          <Button 
            icon={<SyncOutlined />} 
            onClick={refreshTasks}
          >
            刷新
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          pagination={{ 
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个任务`
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* 结构查看器模态框 */}
      <Modal
        title="EquiDock结构查看器"
        open={viewerVisible}
        onCancel={() => setViewerVisible(false)}
        width={1200}
        centered
        bodyStyle={{ padding: 0, height: '80vh', overflow: 'hidden' }}
        footer={null}
      >
        {selectedPdb && (
          <div style={{ width: '100%', height: '100%' }}>
            <MolStarViewer pdbData={selectedPdb} height="auto" />
          </div>
        )}
      </Modal>
    </div>
  );
}
